import FixedLoader from "components/FixedLoader";
import { RequestACallProvider } from "components/RequestACall";
import { useAuthContext } from "context/AuthContext";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { hasAuthParams, useAuth } from "react-oidc-context";
import { useLocation, useNavigate } from "react-router-dom";
import Routes from "./routes";
import { useApolloClient } from "@apollo/client";
import { GetScreensDocument, GetScreensQuery } from "api";
import axios from "axios";
import { useConfirm } from "material-ui-confirm";

const allowLogin = () => {
  const url = window.location.href;
  if (url.includes("/auth/callback")) return false;
  if (url.includes("/auth/complete-profile")) return false;
  if (url.includes("/download")) return false;
  if (url.includes("/terms")) return false;
  if (url.includes("/privacy")) return false;
  if (url.includes("/migrations")) return false;
  if (url.includes("/locallogout")) return false;
  if (url.includes("/analyst")) return false;
  if (url.includes("/plans")) return false;
  return true;
};

function removeQueryParams(url: string, paramsToRemove: string[]): string {
  const urlParts = url.split("?");
  if (urlParts.length < 2) {
    // No query params in the URL
    return url;
  }

  const baseUrl = urlParts[0];
  const queryParams = urlParts[1].split("&");
  const updatedParams = queryParams.filter((param) => {
    const paramName = param.split("=")[0];
    return !paramsToRemove.includes(paramName);
  });

  if (updatedParams.length === 0) {
    // If there are no query params left, return the base URL
    return baseUrl;
  }

  const updatedUrl = baseUrl + "?" + updatedParams.join("&");
  return updatedUrl;
}

const App: FC = () => {
  const {
    isAuthenticated,
    isLoading,
    activeNavigator,
    error,
    signinRedirect,
    signinSilent,
    events,
    signoutSilent,
  } = useAuth();
  const confirm = useConfirm();
  const [loading, setLoading] = useState(false);
  const { removeUserInfo } = useAuthContext();
  const navigate = useNavigate();
  const { search } = useLocation();
  const client = useApolloClient();

  /**
   * Check Session & Logout
   * https://brockallen.com/2016/08/12/check-session-support-in-oidc-client-js
   * Called on OP Signed Out
   */
  const handleAutoLogout = useCallback(() => {
    removeUserInfo();
    signoutSilent();
    navigate("/", { replace: true });
  }, [removeUserInfo, signoutSilent, navigate]);

  // Automatically sign-in
  useEffect(() => {
    // Remove OLD Refresh Token
    localStorage.removeItem("refreshToken");
    if (error?.message?.includes("login_required")) return;

    if (
      !hasAuthParams() &&
      !isAuthenticated &&
      !activeNavigator &&
      !isLoading
    ) {
      if (allowLogin()) {
        signinRedirect({ prompt: "none" });
      }
    }
  }, [
    isAuthenticated,
    activeNavigator,
    isLoading,
    signinRedirect,
    error?.message,
  ]);

  useEffect(() => {
    if (error?.message?.includes("invalid_grant")) {
      confirm({
        title: "Your session has expired!",
        description: "Sign in to continue",
        confirmationText: "Sign in",
        cancellationText: "Logout",
        confirmationButtonProps: {
          variant: "contained",
          disableElevation: true,
          autoFocus: true,
          size: "small",
        },
        cancellationButtonProps: {
          color: "error",
          size: "small",
          disableElevation: true,
        },
        allowClose: true,
        dialogProps: {
          PaperProps: {
            sx: {
              maxWidth: 400,
            },
          },
        },
      })
        .then(() => {
          signinRedirect();
        })
        .catch(() => {
          handleAutoLogout();
        });
    }
  }, [error?.message, confirm, handleAutoLogout, signinRedirect]);

  const searchParams = useMemo(() => new URLSearchParams(search), [search]);

  const handleRefersh = useCallback(async () => {
    try {
      setLoading(true);
      const user = await signinSilent();
      if (user?.access_token) {
        const { data } = await client.query<GetScreensQuery>({
          query: GetScreensDocument
        })
        if ((data?.getScreens ?? [])?.length > 0) {
          navigate(`/onboarding`, { replace: true });
        } else {
          const paramsToRemove = ["OrderCode", "TransactionCode"];
          (window as Window).location = removeQueryParams(
            window.location.href,
            paramsToRemove
          );
        }
      }
    } catch (e) {
      if (axios.isAxiosError(e)) {
        console.error(e.response);
      }
    } finally {
      setLoading(false);
    }
  }, [navigate, signinSilent, client]);

  useEffect(() => {
    if (searchParams.has("OrderCode") && searchParams.has("TransactionCode")) {
      handleRefersh();
    }
  }, [searchParams, handleRefersh]);

  useEffect(() => {
    events.addUserSignedOut(handleAutoLogout);
    return () => {
      events.removeUserSignedOut(handleAutoLogout);
    };
  }, [events, handleAutoLogout]);

  if (isLoading || loading) return <FixedLoader />;
  return (
    <RequestACallProvider>
      <Routes />
    </RequestACallProvider>
  );
};

export default App;
